import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Receipt, Plus, Filter, Calendar } from 'lucide-react';
import { CardStats, ExportDropdown } from '@components/ui';
import { useExportExcel } from '@hooks';
import {
    getExpensesByConstructionId,
    filterExpensesByDate,
    getExpenseStats,
} from '@mocks/harcamalar';
import clsx from 'clsx';
import HarcamalarTablosu from './HarcamalarTablosu';
import HarcamaModal from './HarcamaModal';

const ITEMS_PER_PAGE = 10;

export default function Expenses() {
    const navigate = useNavigate();
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const constructionId = showAllConstructions ? null : selectedConstruction?.id;

    const [dateFilter, setDateFilter] = useState('month');
    const [showAddModal, setShowAddModal] = useState(false);

    // Export hook
    const { exportToExcel, isExporting } = useExportExcel();

    const allExpenses = getExpensesByConstructionId(constructionId);
    const filteredExpenses = useMemo(() =>
        filterExpensesByDate(allExpenses, dateFilter),
        [allExpenses, dateFilter]
    );

    const stats = getExpenseStats(filteredExpenses);

    // Filtre değişince sayfa sıfırla
    const handleFilterChange = (filter) => {
        setDateFilter(filter);
    };

    const dateFilters = [
        { key: 'today', label: 'Bugün' },
        { key: 'week', label: 'Bu Hafta' },
        { key: 'month', label: 'Bu Ay' },
        { key: '3months', label: '3 Ay' },
    ];

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-text-primary">Harcamalar</h1>
                    <p className="text-text-tertiary mt-1">
                        {showAllConstructions
                            ? 'Tüm inşaatlar - Günlük harcama kayıtları'
                            : `${selectedConstruction?.name || ''} - Günlük harcamalar`}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {/* Export */}
                    <ExportDropdown
                        onExportExcel={() => exportToExcel({
                            data: filteredExpenses,
                            columns: [
                                { header: 'Tarih', key: 'date', format: 'date' },
                                { header: 'Kategori', key: 'category' },
                                { header: 'Açıklama', key: 'description' },
                                { header: 'Ödeme Yöntemi', key: 'paymentMethod' },
                                { header: 'Tutar', key: 'amount', format: 'currency' },
                            ],
                            fileName: 'harcamalar_raporu',
                            sheetName: 'Harcamalar',
                        })}
                        isExporting={isExporting}
                        disabled={filteredExpenses.length === 0}
                    />
                    {/* Date Filter */}
                    <div className="flex items-center gap-1 bg-bg-tertiary rounded-lg p-1">
                        {dateFilters.map((filter) => (
                            <button
                                key={filter.key}
                                onClick={() => handleFilterChange(filter.key)}
                                className={clsx(
                                    "px-3 py-1.5 text-sm rounded-md transition-colors",
                                    dateFilter === filter.key
                                        ? "bg-primary text-white"
                                        : "text-text-secondary hover:bg-bg-hover"
                                )}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary-dark transition-colors"
                    >
                        <Plus size={18} />
                        <span className="font-medium">Harcama Ekle</span>
                    </button>
                </div>
            </div >

            {/* Stats */}
            < div className="grid grid-cols-1 sm:grid-cols-3 gap-4" >
                <CardStats
                    title={dateFilters.find(f => f.key === dateFilter)?.label || 'Toplam'}
                    value={formatCurrency(stats.total)}
                    icon={Receipt}
                    color="secondary"
                    size="lg"
                />
                <CardStats
                    title="İşlem Sayısı"
                    value={stats.count.toString()}
                    icon={Calendar}
                    color="tertiary"
                    size="lg"
                />
                <CardStats
                    title="En Çok Harcama"
                    value={stats.byCategory.sort((a, b) => b.total - a.total)[0]?.label || '-'}
                    icon={Filter}
                    color="primary"
                    size="lg"
                />
            </div >

            {/* Harcama Tablosu */}
            <HarcamalarTablosu key={dateFilter} expenses={filteredExpenses} />

            {/* Harcama Ekleme Modal */}
            <HarcamaModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
            />
        </div>
    );
}
